import React, { useEffect, useState } from 'react';
import { useRoleStore } from '@/lib/store';
import { useSettings, useUpdateSettings } from '@/hooks/use-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Loader2, Lock, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function FinancialsTab() {
  const role = useRoleStore((s) => s.role);
  const canEdit = role === 'Admin';
  const { data: settings } = useSettings(true);
  const updateSettings = useUpdateSettings();

  const [rate, setRate] = useState(15);
  const [penalty, setPenalty] = useState(5);
  const [retentionYears, setRetentionYears] = useState(7);
  const [backupEnabled, setBackupEnabled] = useState(true);
  const [backupDay, setBackupDay] = useState(6);
  const [territories, setTerritories] = useState<string[]>([]);
  const [newTerritory, setNewTerritory] = useState('');

  useEffect(() => {
    if (settings) {
      setRate(settings.standardInterestRate);
      setPenalty(settings.defaultPenalty);
      setRetentionYears(settings.retentionYears);
      setBackupEnabled(settings.backupEnabled ?? true);
      setBackupDay(settings.backupDayOfWeek ?? 6);
      setTerritories(settings.activeTerritories || []);
    }
  }, [settings]);

  const handleSave = () => {
    updateSettings.mutate({
      standardInterestRate: rate,
      defaultPenalty: penalty,
      retentionYears,
      backupEnabled,
      backupDayOfWeek: backupDay,
      activeTerritories: territories,
    });
  };

  const addTerritory = () => {
    const t = newTerritory.trim();
    if (!t) return;
    if (territories.includes(t)) { toast.error('Territory already exists'); return; }
    setTerritories([...territories, t]);
    setNewTerritory('');
  };

  const removeTerritory = (t: string) => setTerritories(territories.filter((x) => x !== t));

  return (
    <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
      <CardHeader className="border-b bg-muted/30 p-8">
        <CardTitle className="text-foreground font-black uppercase tracking-tight">Financial & Operational Policies</CardTitle>
        <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Interest rate, penalty, data retention, backup schedule, and territory configuration.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-10 p-6 sm:p-10">
        {/* Interest Rate */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-black uppercase tracking-tight text-foreground">Standard Interest Rate</Label>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Annual percentage applied to microfinance loans.</p>
            </div>
            <span className="text-3xl font-black text-primary">{rate}%</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="py-4"><Slider value={[rate]} onValueChange={([v]) => setRate(v)} max={30} step={0.5} disabled={!canEdit} /></div>
              </TooltipTrigger>
              {!canEdit && <TooltipContent className="border-none bg-destructive text-destructive-foreground"><p className="flex items-center gap-2"><Lock className="h-3 w-3" />Read-only</p></TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Default Penalty */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-black uppercase tracking-tight text-foreground">Default Penalty Rate</Label>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Percentage charged on overdue accounts.</p>
            </div>
            <span className="text-3xl font-black text-destructive">{penalty}%</span>
          </div>
          <Slider value={[penalty]} onValueChange={([v]) => setPenalty(v)} max={20} step={0.5} disabled={!canEdit} />
        </div>

        {/* Retention Years */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-black uppercase tracking-tight text-foreground">Data Retention (Years)</Label>
            <Input type="number" min={1} max={20} value={retentionYears} onChange={(e) => setRetentionYears(Number(e.target.value))} disabled={!canEdit} className="h-12 rounded-xl border-border bg-muted/30 font-bold" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-black uppercase tracking-tight text-foreground">Backup Day</Label>
            <Select value={String(backupDay)} onValueChange={(v) => setBackupDay(Number(v))} disabled={!canEdit}>
              <SelectTrigger className="h-12 rounded-xl border-border bg-muted/30"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                {DAYS.map((d, i) => <SelectItem key={i} value={String(i)} className="font-bold">{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Backup Toggle */}
        <div className="flex items-center gap-4 rounded-2xl border border-border bg-muted/30 p-5">
          <input type="checkbox" checked={backupEnabled} onChange={(e) => setBackupEnabled(e.target.checked)} disabled={!canEdit} className="h-5 w-5 rounded border-border text-primary focus:ring-primary" />
          <div>
            <p className="font-black uppercase tracking-tight text-foreground">Automated Backups</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Enable scheduled weekly data exports.</p>
          </div>
        </div>

        {/* Active Territories */}
        <div className="space-y-4">
          <Label className="text-xs font-black uppercase tracking-tight text-foreground">Active Territories</Label>
          <div className="flex flex-wrap gap-2">
            {territories.map((t) => (
              <Badge key={t} className="border-none bg-secondary px-3 py-1.5 text-[10px] font-black uppercase">
                {t}
                {canEdit && <button onClick={() => removeTerritory(t)} className="ml-2 text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>}
              </Badge>
            ))}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Input placeholder="New territory name" value={newTerritory} onChange={(e) => setNewTerritory(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTerritory())} className="h-10 rounded-xl border-border bg-muted/30 font-bold" />
              <Button variant="outline" onClick={addTerritory} className="h-10 rounded-xl border-border font-black uppercase"><Plus className="mr-1 h-4 w-4" />Add</Button>
            </div>
          )}
        </div>

        {/* Save / Read-only notice */}
        {canEdit ? (
          <Button onClick={handleSave} disabled={updateSettings.isPending} className="h-14 w-full rounded-xl bg-primary px-10 font-black uppercase tracking-tight text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 sm:w-auto">
            {updateSettings.isPending && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}Save Financial Policies
          </Button>
        ) : (
          <div className="flex items-start gap-3 rounded-xl bg-muted/50 p-4">
            <Lock className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-bold uppercase text-muted-foreground">View-only. Admin authorization required to update policies.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
